import type { ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  CalendarClock,
  Globe,
  Link2,
  ReceiptText,
  ShieldCheck,
  SplitSquareHorizontal,
  Users,
  Wallet,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/ui/brand-mark";
import { PhoneFrame } from "../components/PhoneFrame";
import { AppleIcon, GoogleIcon } from "../components/auth/ProviderIcons";
import { useAuth } from "../auth/AuthContext";
import {
  screenshotDashboard,
  screenshotGroupDetail,
  screenshotGroups,
  screenshotExpenseDetail,
} from "../assets";

export function LandingPage(): ReactNode {
  const { error, login, status } = useAuth();
  const [params] = useSearchParams();
  const rawReturnTo = params.get("returnTo");
  // Only honour same-origin paths to stop a tampered query parameter from
  // becoming an open-redirect into a third-party site.
  const returnTo =
    rawReturnTo && rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//")
      ? rawReturnTo
      : "/";

  if (status === "authenticated") {
    return <Navigate to={returnTo} replace />;
  }

  const signingIn = status === "loading";

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <SiteHeader />

      <main>
        <Hero
          error={error}
          signingIn={signingIn}
          onGoogle={() => void login(returnTo, "Google")}
          onApple={() => void login(returnTo, "SignInWithApple")}
        />
        <LogosStrip />
        <Features />
        <Showcase />
        <FinalCta
          signingIn={signingIn}
          onGoogle={() => void login(returnTo, "Google")}
          onApple={() => void login(returnTo, "SignInWithApple")}
        />
      </main>

      <SiteFooter />
    </div>
  );
}

function SiteHeader(): ReactNode {
  return (
    <header className="sticky top-0 z-30 border-b border-outline-variant/40 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <BrandMark />
          <span className="text-headline-md text-primary">Tailspend</span>
        </div>
        <nav className="flex items-center gap-2">
          <Link
            to="/about"
            className="hidden rounded-md px-3 py-2 text-label-md text-on-surface-variant transition-colors hover:text-primary sm:inline-block"
          >
            About
          </Link>
          <a href="#features" className="hidden sm:inline-block">
            <Button variant="ghost" size="sm">
              Features
            </Button>
          </a>
        </nav>
      </div>
    </header>
  );
}

interface CtaHandlers {
  signingIn: boolean;
  onGoogle: () => void;
  onApple: () => void;
}

function Hero({
  error,
  signingIn,
  onGoogle,
  onApple,
}: CtaHandlers & { error: string | null }): ReactNode {
  return (
    <section className="relative overflow-hidden">
      {/* Soft brand glows behind the hero. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute -left-24 -top-24 size-96 rounded-full bg-primary-fixed/60 blur-3xl" />
        <div className="absolute -right-24 top-12 size-96 rounded-full bg-tertiary-fixed/40 blur-3xl" />
      </div>

      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-2 lg:gap-8">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/60 bg-surface-container-lowest px-3 py-1 text-label-sm text-on-surface-variant">
            <span className="size-1.5 rounded-full bg-tertiary" />
            Splitting expenses, minus the spreadsheet
          </span>

          <h1 className="mt-6 text-[clamp(2.5rem,7vw,4rem)] font-extrabold leading-[1.05] tracking-[-0.03em]">
            Tailspend
          </h1>
          <p className="mt-5 text-body-lg text-on-surface-variant">
            Track shared expenses with friends, roommates, and trips.
          </p>
          <p className="mt-3 text-body-md text-on-surface-variant">
            Create a group, log who paid, and Tailspend keeps every balance
            clear and settled — across people, currencies, and recurring bills.
          </p>

          {error ? (
            <p
              role="alert"
              className="mt-5 rounded-sm bg-error-container px-4 py-3 text-body-md text-on-error-container"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-8 flex max-w-sm flex-col gap-3">
            <Button
              variant="secondary"
              size="lg"
              className="w-full border border-google-outline bg-google-surface text-google-on-surface shadow-sm hover:bg-google-hover focus-visible:ring-google-focus"
              onClick={onGoogle}
              disabled={signingIn}
            >
              <GoogleIcon />
              Continue with Google
            </Button>
            <Button
              size="lg"
              className="w-full border border-apple-surface bg-apple-surface text-apple-on-surface shadow-sm hover:bg-apple-hover focus-visible:ring-apple-surface"
              onClick={onApple}
              disabled={signingIn}
            >
              <AppleIcon />
              Continue with Apple
            </Button>
            <p className="text-center text-label-sm text-on-surface-variant">
              Free to use · No password · We never see your bank
            </p>
          </div>

          <div className="mt-6">
            <Link
              to="/about"
              className="text-label-md text-primary underline-offset-4 hover:underline"
            >
              Learn more about Tailspend
            </Link>
          </div>
        </div>

        {/* Hero device pair. */}
        <div className="relative mx-auto flex w-full max-w-md items-end justify-center gap-4 lg:max-w-none">
          <PhoneFrame
            src={screenshotDashboard}
            alt="Tailspend dashboard showing your balance across active groups"
            priority
            className="w-3/4 max-w-[15rem] translate-y-4 sm:w-1/2"
          />
          <PhoneFrame
            src={screenshotGroupDetail}
            alt="A Tailspend group with its running expense list"
            className="hidden w-1/2 max-w-[15rem] -translate-y-4 sm:block"
          />
        </div>
      </div>
    </section>
  );
}

function LogosStrip(): ReactNode {
  const points = [
    "Trips & holidays",
    "Shared households",
    "Group dinners",
    "Sports teams",
    "Side projects",
  ];
  return (
    <section className="border-y border-outline-variant/40 bg-surface-container-lowest">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-5 py-6 sm:px-8">
        <span className="text-label-sm text-on-surface-variant">Made for</span>
        {points.map((p) => (
          <span key={p} className="text-label-md text-on-surface-variant/90">
            {p}
          </span>
        ))}
      </div>
    </section>
  );
}

function Features(): ReactNode {
  const features = [
    {
      icon: <SplitSquareHorizontal className="size-5" />,
      title: "Split any way you like",
      description:
        "Divide a bill equally, by exact amounts, percentages, or shares — down to individual items.",
    },
    {
      icon: <ReceiptText className="size-5" />,
      title: "Itemised expenses",
      description:
        "Record who paid and exactly how each line is shared, with comments to keep everyone in the loop.",
    },
    {
      icon: <Globe className="size-5" />,
      title: "Multi-currency",
      description:
        "Spend across currencies on a trip; Tailspend keeps each group's totals clear in its own currency.",
    },
    {
      icon: <CalendarClock className="size-5" />,
      title: "Recurring bills",
      description:
        "Rent, subscriptions, and standing costs post on schedule so nothing slips through the cracks.",
    },
    {
      icon: <Link2 className="size-5" />,
      title: "Share a link",
      description:
        "Invite people with one link, or share a read-only view with anyone who just needs to follow along.",
    },
    {
      icon: <ShieldCheck className="size-5" />,
      title: "Private by default",
      description:
        "Sign in with Google or Apple. We store only what your group needs — and never your password.",
    },
  ];

  return (
    <section
      id="features"
      className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24"
    >
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-label-sm text-on-surface-variant">Why Tailspend</p>
        <h2 className="mt-3 text-headline-lg">
          Everything a shared wallet needs
        </h2>
        <p className="mt-4 text-body-md text-on-surface-variant">
          Stronger group controls and explicit spending limits than a
          spreadsheet — without the busywork.
        </p>
      </div>

      <div className="mt-12 grid gap-bento-gap sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f) => (
          <div
            key={f.title}
            className="rounded-md border border-outline-variant/50 bg-surface-container-lowest p-6 transition-colors hover:border-primary/40"
          >
            <span className="grid size-11 place-items-center rounded-sm bg-primary-fixed text-primary">
              {f.icon}
            </span>
            <h3 className="mt-5 text-headline-md">{f.title}</h3>
            <p className="mt-2 text-body-md text-on-surface-variant">
              {f.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Showcase(): ReactNode {
  const shots = [
    {
      src: screenshotGroups,
      alt: "The Tailspend groups screen listing every group and its balance",
      title: "All your groups, one tap away",
      description:
        "Trips, flatmates, teams — each with its own balance and currency.",
      icon: <Users className="size-5" />,
    },
    {
      src: screenshotExpenseDetail,
      alt: "A Tailspend expense detail showing payers, an itemised split, and comments",
      title: "Every expense, fully itemised",
      description:
        "See who paid, how it was split, and the conversation around it.",
      icon: <ReceiptText className="size-5" />,
    },
    {
      src: screenshotGroupDetail,
      alt: "A Tailspend group screen with the running expense list and balance",
      title: "Know your balance at a glance",
      description:
        "A clear running total of what you owe and what's coming back to you.",
      icon: <Wallet className="size-5" />,
    },
  ];

  return (
    <section className="bg-surface-container-lowest">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-label-sm text-on-surface-variant">
            See it in action
          </p>
          <h2 className="mt-3 text-headline-lg">
            Built for the phone in your hand
          </h2>
          <p className="mt-4 text-body-md text-on-surface-variant">
            The same Tailspend, wherever your group is — on the web and as a
            native iOS and Android app.
          </p>
        </div>

        <div className="mt-14 grid gap-12 sm:gap-10 lg:grid-cols-3">
          {shots.map((s) => (
            <div key={s.src} className="flex flex-col items-center text-center">
              <PhoneFrame
                src={s.src}
                alt={s.alt}
                className="w-full max-w-[16rem]"
              />
              <span className="mt-8 grid size-10 place-items-center rounded-sm bg-primary-fixed text-primary">
                {s.icon}
              </span>
              <h3 className="mt-4 text-headline-md">{s.title}</h3>
              <p className="mt-2 max-w-xs text-body-md text-on-surface-variant">
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ signingIn, onGoogle, onApple }: CtaHandlers): ReactNode {
  return (
    <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
      <div className="relative overflow-hidden rounded-lg bg-primary px-6 py-14 text-on-primary sm:px-12">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-0"
        >
          <div className="absolute -right-16 -top-16 size-64 rounded-full bg-on-primary/10 blur-2xl" />
          <div className="absolute -bottom-20 -left-10 size-64 rounded-full bg-on-primary/10 blur-2xl" />
        </div>
        <div className="relative mx-auto max-w-xl text-center">
          <h2 className="text-headline-lg">Settle up with your group today</h2>
          <p className="mt-4 text-body-lg text-on-primary/80">
            Create your first group in seconds. No card, no password — just sign
            in and start tracking.
          </p>
          <div className="mx-auto mt-8 flex max-w-sm flex-col gap-3">
            <Button
              variant="secondary"
              size="lg"
              className="w-full border border-google-outline bg-google-surface text-google-on-surface shadow-sm hover:bg-google-hover focus-visible:ring-google-focus"
              onClick={onGoogle}
              disabled={signingIn}
            >
              <GoogleIcon />
              Continue with Google
            </Button>
            <Button
              size="lg"
              className="w-full border border-apple-surface bg-apple-surface text-apple-on-surface shadow-sm hover:bg-apple-hover focus-visible:ring-apple-surface"
              onClick={onApple}
              disabled={signingIn}
            >
              <AppleIcon />
              Continue with Apple
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SiteFooter(): ReactNode {
  return (
    <footer className="border-t border-outline-variant/40">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row sm:px-8">
        <div className="flex items-center gap-2">
          <BrandMark className="size-7" />
          <span className="text-label-md text-on-surface-variant">
            Tailspend
          </span>
        </div>
        <div className="flex items-center gap-6 text-label-md text-on-surface-variant">
          <Link to="/about" className="hover:text-primary">
            About
          </Link>
          <a href="#features" className="hover:text-primary">
            Features
          </a>
          <Link to="/privacy" className="hover:text-primary">
            Privacy
          </Link>
          <Link to="/support" className="hover:text-primary">
            Support
          </Link>
          <Link to="/brand" className="hover:text-primary">
            Brand
          </Link>
        </div>
      </div>
    </footer>
  );
}
